<template>
  <v-container id="purchase-orders" class="align-start" fluid>
    <i-overlay
      v-if="fetching && $vuetifyDisplay.smAndDown"
      text="Loading purchase orders..."
    />

    <v-row v-else id="content" no-gutters>
      <i-local-filter
        class="mb-6"
        :filterPlaceholder="`Search Orders by PO Number or ${
          this.isILR ? 'Customer' : 'Retailer'
        }`"
        @filter="localFilter"
      />

      <v-col cols="12">
        <template v-if="!fetching && filteredPurchaseOrders.length === 0">
          <i-empty-results
            buttonText="Dashboard"
            buttonRouteName="dashboard"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <v-row no-gutters>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <div
                  class="mt-2 pa-0"
                  v-for="item in filteredPurchaseOrders"
                  :key="item.id"
                >
                  <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span
                          ><strong>{{ headers[0].text }}</strong></span
                        >
                      </v-col>
                      <v-col cols="6" class="pl-4">
                        <span
                          ><strong>{{
                            $filters.text(item.supplierOrderNumber)
                          }}</strong></span
                        >
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[1].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.createdAt)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[2].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(
                            item.actualFinalConfirmationDate
                          )
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>
                          {{ headers[3].text }}
                          <v-icon
                            class="i-tooltip-color pb-1"
                            small
                            v-tooltip="'Includes fees'"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.currency(displayOrderTotal(item.summary))
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[4].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <i-payment-type
                          v-if="item.paymentType"
                          :paymentType="item.paymentType"
                          :textOnly="true"
                        />
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[5].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ $filters.text(item.store.name) }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[6].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.postCode)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[7].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.state)
                        }}</span>
                      </v-col>
                    </v-row>

                    <v-divider class="mt-2 mb-4"></v-divider>
                    <template v-if="isServiceOrder(item)">
                      <v-row no-gutters align="center">
                        <v-col cols="6" class="py-1">
                          <span>ORDER TYPE</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip color="secondary" small> SERVICE </v-chip>
                        </v-col>
                      </v-row>
                    </template>
                    <v-btn
                      rounded
                      class="i-btn-block-orange g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                    >
                      <strong>View & Confirm</strong>
                    </v-btn>

                    <div class="text-center pt-3">
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="!item.confirmedPdfAvailable"
                        @click="fetchPDF(item.id)"
                      >
                        <span>Export Purchase Order</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                    <div v-if="!isServiceOrder(item)" class="text-center pt-1">
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="!item.confirmedPdfAvailable"
                        @click="fetchPDF(item.id, true)"
                      >
                        <span>Export Delivery Note</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                  </v-card>
                </div>
              </template>

              <template v-else>
                <v-data-table
                  class="pt-2 elevation-1 i-data-table"
                  :headers="headers"
                  :items="filteredPurchaseOrders"
                  :loading="fetching"
                  :items-per-page="-1"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                >
                  <template
                    v-slot:[`header.summary.remittanceTotal.totalIncludingTax`]="{
                      header
                    }"
                  >
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pb-1"
                      small
                      v-tooltip="'Includes fees'"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                    <span>{{ $filters.text(item.supplierOrderNumber) }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template
                    v-slot:[`item.actualFinalConfirmationDate`]="{ item }"
                  >
                    <span>{{
                      $filters.formatDateTime12h(
                        item.actualFinalConfirmationDate
                      )
                    }}</span>
                  </template>

                  <template
                    v-slot:[`item.summary.remittanceTotal.totalIncludingTax`]="{
                      item
                    }"
                  >
                    <span>{{
                      $filters.currency(displayOrderTotal(item.summary))
                    }}</span>
                  </template>

                  <template v-slot:[`item.paymentType`]="{ item }">
                    <i-payment-type
                      v-if="item.paymentType"
                      :paymentType="item.paymentType"
                      :textOnly="true"
                    />
                  </template>

                  <template v-slot:[`item.store.name`]="{ item }">
                    <span>{{ $filters.text(item.store.name) }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.postCode`]="{ item }">
                    <span>{{
                      $filters.text(item.deliveryAddress.postCode)
                    }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.state`]="{ item }">
                    <span>{{ $filters.text(item.deliveryAddress.state) }}</span>
                  </template>
                  <template v-slot:[`item.orderType`]="{ item }">
                    <v-chip v-if="isServiceOrder(item)" color="secondary" small>
                      SERVICE
                    </v-chip>
                  </template>
                  <template v-slot:[`item.action`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-orange g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                    >
                      Action
                    </v-btn>
                  </template>

                  <template v-slot:[`item.details`]="{ item }">
                    <v-icon
                      class="i-btn-pdf mr-1"
                      :disabled="!item.confirmedPdfAvailable"
                      v-tooltip="'Purchase Order PDF'"
                      @click="fetchPDF(item.id)"
                    >
                      mdi-file-pdf-box
                    </v-icon>
                    <v-icon
                      v-if="!isServiceOrder(item)"
                      class="i-btn-pdf mr-1"
                      :disabled="!item.confirmedPdfAvailable"
                      v-tooltip="'Delivery Note PDF'"
                      @click="fetchPDF(item.id, true)"
                    >
                      mdi-truck-check
                    </v-icon>
                  </template>
                </v-data-table>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { fetchOrderPDF } from '@/services/supplierOrders'
import iOverlay from '@/components/Overlay'
import iLocalFilter from '@/components/LocalFilter'
import { openFile } from '@/utils/common'
import iEmptyResults from '@/components/EmptyResults'
import iPaymentType from '@/components/PaymentType'
import { pickSupplierCostTotal } from '@/utils/supplierAmountDisplay'

const LINK_MAX_LENGTH = 30
const VIEW_STATUSES = {
  BEFORE_DELIVERY: 'BEFORE_DELIVERY',
  DURING_DELIVERY: 'DURING_DELIVERY'
}

export default {
  compatConfig: {
    WATCH_ARRAY: false
  },

  components: {
    iPaymentType,
    iEmptyResults,
    iOverlay,
    iLocalFilter
  },

  props: {
    fetching: {
      default: false,
      type: Boolean
    },

    orders: {
      default: () => [],
      type: Array
    }
  },

  data() {
    return {
      emptyFilteredResultsText: 'No DELIVERED Purchase Orders found',
      purchaseOrders: [],
      filterText: '',
      linkMaxLength: LINK_MAX_LENGTH,
      ...VIEW_STATUSES
    }
  },

  computed: {
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),
    headers() {
      return [
        {
          text: 'PO NUMBER',
          value: 'supplierOrderNumber',
          align: 'start',
          width: '9%'
        },
        {
          text: 'ORDER DATE & TIME',
          value: 'createdAt',
          align: 'center',
          width: '12%'
        },
        {
          text: 'DRAFT PO CONFIRMED DATE',
          value: 'actualFinalConfirmationDate',
          align: 'center',
          width: '12%'
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'PO TOTAL (INC TAX)'
            : 'PO TOTAL',
          value: 'summary.remittanceTotal.totalIncludingTax',
          align: 'center',
          width: '11%'
        },
        {
          text: 'PAYMENT TYPE',
          value: 'paymentType',
          align: 'center',
          sortable: false,
          width: '9%'
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'store.name',
          width: '20%'
        },
        {
          text: 'POSTCODE',
          value: 'deliveryAddress.postCode',
          align: 'center',
          width: '9%'
        },
        {
          text: 'STATE',
          value: 'deliveryAddress.state',
          align: 'center',
          width: '9%'
        },
        {
          text: 'DELIVERED DATE',
          value: 'action',
          align: 'center',
          sortable: false,
          width: 175
        },
        {
          text: 'ORDER TYPE',
          value: 'orderType',
          align: 'center',
          sortable: false,
          width: '8%'
        },
        {
          text: 'EXPORT',
          value: 'details',
          align: 'center',
          sortable: false,
          width: '8%'
        }
      ]
    },

    filteredPurchaseOrders() {
      return this.purchaseOrders.filter((order) => {
        return (
          order.supplierOrderNumber
            .toLowerCase()
            .includes(this.filterText.toLowerCase()) ||
          order.store.name.toLowerCase().includes(this.filterText.toLowerCase())
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    displayOrderTotal(summary) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: summary?.remittanceTotal
      })
    },

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },
    isServiceOrder(order) {
      return order?.supplierOrderItems?.some(
        (item) => item.productType === 'SERVICE'
      )
    },
    fetchPDF(orderId, getDeliveryNote) {
      fetchOrderPDF(orderId, getDeliveryNote)
        .then((response) => {
          const file = new Blob([response], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Order ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    truncateString(str, length) {
      return str.length > length ? str.substr(0, length - 3) + '...' : str
    }
  },

  mounted() {
    this.purchaseOrders = _.cloneDeep(this.orders)
  },

  watch: {
    orders: {
      deep: true,
      handler() {
        // need watcher as orders is an async prop
        this.purchaseOrders = _.cloneDeep(this.orders)
      }
    }
  }
}
</script>
